import { CheckCircle2, XCircle, HelpCircle, MinusCircle } from 'lucide-preact'
import type { AssetQaCheckStatus, AssetQaSeverity, AssetQaVerdict } from '@/types/assetQa'

/**
 * How a QA result is presented. One place, because the same verdict appears in the top bar and the
 * side panel and the two disagreeing would be worse than either being wrong.
 */

export const QA_VERDICT_META: Record<AssetQaVerdict, { label: string; classes: string; tone: 'bad' | 'warn' | 'good' }> = {
    'Do Not Submit Yet': {
        label: 'Do not submit yet',
        classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        tone: 'bad',
    },
    'Fix Before Submitting': {
        label: 'Fix before submitting',
        classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        tone: 'warn',
    },
    'Verified Where Possible': {
        label: 'Verified where possible',
        classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        tone: 'warn',
    },
    'Ready To Submit': {
        label: 'Ready to submit',
        classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        tone: 'good',
    },
}

export const QA_SEVERITY_META: Record<AssetQaSeverity, { classes: string }> = {
    Critical: { classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
    Major: { classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
    Minor: { classes: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
}

/**
 * ⚑ 'Needs Human Verification' gets its own icon and its own colour — never a tick and never a
 * silent omission. It is the rulebook's core rule: a check that could not run says so. Collapsing
 * it into a pass here would make every tick in the grid meaningless, which is the one failure this
 * whole feature cannot survive.
 *
 * ⚑⚑ These labels are the matrix's §1 status names, and two of them had drifted (ORB-987). The
 * invented one, "Needs a human to check", is the phrase at the centre of the 30 Sep incident: the
 * matrix defines ⚠️ as *"the check could not be performed with the material available"*, and our
 * label promised an action that state does not have. Marjo spent a day looking for the override
 * button it implied. The matrix owns this vocabulary — do not reword it here.
 *
 * ⚑ 'Does not apply' has no matrix equivalent: Not Applicable is a platform-only outcome for a
 * check that does not exist for an asset type, so there is nothing to match and nothing drifting.
 */
export const QA_CHECK_STATUS_META: Record<
    AssetQaCheckStatus,
    { label: string; Icon: typeof CheckCircle2; iconClasses: string }
> = {
    'Verified Pass': {
        label: 'Verified pass',
        Icon: CheckCircle2,
        iconClasses: 'text-green-600 dark:text-green-400',
    },
    'Flagged': {
        label: 'Flagged',
        Icon: XCircle,
        iconClasses: 'text-red-600 dark:text-red-400',
    },
    'Needs Human Verification': {
        label: 'Needs human verification',
        Icon: HelpCircle,
        iconClasses: 'text-amber-600 dark:text-amber-400',
    },
    'Not Applicable': {
        label: 'Does not apply',
        Icon: MinusCircle,
        iconClasses: 'text-slate-400 dark:text-slate-500',
    },
}
